<template>
  <main class="main main--has-floating-buttons">
    <tabable-card cols="12" @activeCardIndex="activeCardIndex = $event">
      <tabable-card-content :title="$t('pageCockpit.card.cockpit')" cols="12">
        <hm-card-inner>
          <hm-col :grid="true" cols="12">
            <hm-input-date v-model="startDate" :label="$t('pageCockpit.startDate')" cols="2" />
            <hm-input-date :min-date="startDate"
                           v-model="endDate"
                           :label="$t('pageCockpit.endDate')"
                           :help="$t('pageCockpit.endDate_help')"
                           cols="2" />
            <hm-input-select v-model="comparisonYearOffset"
                             :lazy="true"
                             :label="$t('pageCockpit.chart.previousYear')"
                             name="comparisonYearOffset"
                             cols="2">
              <option :value="-1">{{ formatDateRange(-1) }}</option>
              <option :value="-2">{{ formatDateRange(-2) }}</option>
              <option :value="-3">{{ formatDateRange(-3) }}</option>
              <option :value="-4">{{ formatDateRange(-4) }}</option>
              <option :value="-5">{{ formatDateRange(-5) }}</option>
            </hm-input-select>
          </hm-col>
        </hm-card-inner>
        <hm-card-inner :full-size="true">
          <spinner-overlay v-if="isEmpty(chartData) || isEmpty(occupancyChartData)" />
          <hm-col :grid="true" cols="12" class="grid--dense">
            <hm-col :expandable="expandable"
                    :divider="true"
                    cols="12 6@tablet 4@desktop"
                    @resize="updateChart('chart1')"
                    @expand="expandable = false"
                    @shrink="expandable = true">
              <GChart v-if="!isEmpty(chartData)"
                      ref="chart1"
                      :data="chartData"
                      :options="chartOptions"
                      :settings="{ packages: ['corechart', 'line'] }"
                      :resize-debounce="250"
                      class="google-chart google-chart--line"
                      type="LineChart" />
            </hm-col>
            <hm-col :expandable="expandable"
                    :divider="true"
                    cols="12 6@tablet 4@desktop"
                    @resize="updateChart('chart2')"
                    @expand="expandable = false"
                    @shrink="expandable = true">
              <GChart v-show="!isEmpty(occupancyChartData)"
                      ref="chart2"
                      :data="occupancyChartData"
                      :options="occupancyChartOptions"
                      :settings="{ packages: ['corechart', 'line'] }"
                      :resize-debounce="250"
                      class="google-chart google-chart--line"
                      type="LineChart" />
            </hm-col>
            <hm-col cols="12 6@tablet 4@desktop">
              <GChart v-show="!isEmpty(occupancyGaugeData)"
                      :data="occupancyGaugeData"
                      :options="occupancyGaugeChartOptions"
                      :settings="{ packages: ['corechart', 'gauge'] }"
                      :resize-debounce="250"
                      class="google-chart google-chart--gauge"
                      type="Gauge" />
            </hm-col>
            <!-- TODO: Buchungsquelle / PieChart -->
            <!-- TODO: Durchschnittliche Aufenthaltsdauer / Zahl -->
            <!-- TODO: RevPAR / Zahl -->
            <!-- TODO: RevPAR / LineChart -->
          </hm-col>
        </hm-card-inner>
      </tabable-card-content>
      <tabable-card-content v-if="hasRole('ROLE_SYSADMIN') || hasLicense('PickupReporting')" title="Forecast" @show="shortTerm.debouncedUpdateData">
        <hm-card-inner>
          <hm-input-select v-model="shortTerm.pickupDays" label="Pick-Up" cols="12 6@tablet 3@desktop 2@hd">
            <option :value="1">1d</option>
            <option :value="3" :disabled="!hasPickupLicense">3d</option>
            <option :value="7" :disabled="!hasPickupLicense">7d</option>
          </hm-input-select>
        </hm-card-inner>
        <hm-card-inner :full-size="true" min-height="30rem">
          <spinner-overlay v-if="shortTerm.loading || shortTermPreviousYear.loading" />
          <template v-if="!isNil(shortTerm.data)">
            <hm-table :hover="true" :sticky="true" columns="11">
              <div class="hm-table__th">Datum</div>
              <div class="hm-table__th text-right">Belegung</div>
              <div class="hm-table__th text-right">Zimmernächte</div>
              <div class="hm-table__th text-right">Zimmernächte Pick-Up {{ shortTerm.pickupDays }}d</div>
              <div class="hm-table__th text-right">Prognose (1Y)</div>
              <div class="hm-table__th text-right">ADR</div>
              <div class="hm-table__th text-right">ADR (OTA)</div>
              <div class="hm-table__th text-right">ADR Pick-Up {{ shortTerm.pickupDays }}d</div>
              <div class="hm-table__th text-right">RevPAR</div>
              <div class="hm-table__th text-right">Umsatz</div>
              <div class="hm-table__th text-right">Umsatz Pick-Up {{ shortTerm.pickupDays }}d</div>
              <template v-for="(value, key, idx) in shortTerm.data[today]">
                <template v-if="!isNil(value.hotelReport)">
                  <div :class="[{'weekend': isWeekend(key)}]" class="hm-table__td">{{ key | toFullDate }}</div>
                  <template v-if="!hasPickupLicense && idx > 3">
                    <div :class="[{'weekend': isWeekend(key)}]" class="hm-table__td text-right blur">00,0 %</div>
                    <div :class="[{'weekend': isWeekend(key)}]" class="hm-table__td text-right blur">00</div>
                    <div :class="[{'weekend': isWeekend(key)}]" class="hm-table__td text-right blur">0</div>
                    <div :class="[{'weekend': isWeekend(key)}]" class="hm-table__td text-right blur">000 (+0)</div>
                    <div :class="[{'weekend': isWeekend(key)}]" class="hm-table__td text-right blur">000,00 EUR</div>
                    <div :class="[{'weekend': isWeekend(key)}]" class="hm-table__td text-right blur">000,00 EUR</div>
                    <div :class="[{'weekend': isWeekend(key)}]" class="hm-table__td text-right blur">0,00 EUR</div>
                    <div :class="[{'weekend': isWeekend(key)}]" class="hm-table__td text-right blur">00,00 EUR</div>
                    <div :class="[{'weekend': isWeekend(key)}]" class="hm-table__td text-right blur">0 000,00 EUR</div>
                    <div :class="[{'weekend': isWeekend(key)}]" class="hm-table__td text-right blur">0,00 EUR</div>
                  </template>
                  <template v-else>
                    <div :class="[{'weekend': isWeekend(key)}]" class="hm-table__td text-right">{{ value.hotelReport.current.occupancy | toPercentage }}&nbsp;%</div>
                    <div :class="[{'weekend': isWeekend(key)}]" class="hm-table__td text-right">{{ value.hotelReport.current.roomNights | toInt }}</div>
                    <div :class="[{'weekend': isWeekend(key)}]" class="hm-table__td text-right">{{ value.hotelReport.pickup.roomNights | toInt }}</div>
                    <div :class="[{'weekend': isWeekend(key)}]" class="hm-table__td text-right">{{ shortTermForecast(key, value.hotelReport.current.roomNights, 'roomNights', value.hotelReport.current.availableRoomNights ) }}</div>
                    <div :class="[{'weekend': isWeekend(key)}]" class="hm-table__td text-right">{{ value.hotelReport.current.adr | toCurrency }}&nbsp;{{ hotelSettings.details.currency }}</div>
                    <div :class="[{'weekend': isWeekend(key)}]" class="hm-table__td text-right">{{ value.hotelReport.current.adrOta | toCurrency }}&nbsp;{{ hotelSettings.details.currency }}</div>
                    <div :class="[{'weekend': isWeekend(key)}]" class="hm-table__td text-right">{{ value.hotelReport.pickup.adr | toCurrency }}&nbsp;{{ hotelSettings.details.currency }}</div>
                    <div :class="[{'weekend': isWeekend(key)}]" class="hm-table__td text-right">{{ value.hotelReport.current.revpar | toCurrency }}&nbsp;{{ hotelSettings.details.currency }}</div>
                    <div :class="[{'weekend': isWeekend(key)}]" class="hm-table__td text-right">{{ value.hotelReport.current.lodgingRevenue | toCurrency }}&nbsp;{{ hotelSettings.details.currency }}</div>
                    <div :class="[{'weekend': isWeekend(key)}]" class="hm-table__td text-right">{{ value.hotelReport.pickup.lodgingRevenue | toCurrency }}&nbsp;{{ hotelSettings.details.currency }}</div>
                  </template>
                </template>
              </template>
            </hm-table>
          </template>
        </hm-card-inner>
      </tabable-card-content>
      <tabable-card-content v-if="hasRole('ROLE_SYSADMIN') || hasLicense('PickupReporting')" title="Pickup" @show="pickup.debouncedUpdateData">
        <hm-card-inner :full-size="true">
          <hm-col cols="12">
            <span class="info-text info-text--full-size info-text--no-margin col-12">Die historischen Werte werden aus den Änderungsverläufen berechnet und nicht aus täglichen Snapshots. In Einzelfällen kann es daher zu geringfügigen Abweichungen kommen.</span>
          </hm-col>
        </hm-card-inner>
        <hm-card-inner>
          <hm-input-date v-model="pickup.startDate"
                         :max-date="today"
                         :disabled="!hasPickupLicense"
                         min-date="20220301"
                         label="Datum"
                         cols="12 6@tablet 3@desktop 2@hd" />
          <hm-input-select v-model="pickup.granularity" label="Granularität" cols="12 6@tablet 3@desktop 2@hd">
<!--            <option :disabled="!hasPickupLicense" value="Daily">täglich</option>-->
<!--            <option :disabled="!hasPickupLicense" value="Weekly">wöchentlich</option>-->
            <option value="Monthly">monatlich</option>
            <option :disabled="!hasPickupLicense" value="Quarterly">quartalsweise</option>
          </hm-input-select>
          <hm-input-select v-model="pickup.visibleGroups" label="Spalten" cols="12 6@tablet 3@desktop 2@hd">
            <option v-for="it in 8" :key="it" :value="5+it">{{ 5+it }}</option>
          </hm-input-select>
          <hm-input-select v-model="pickup.mode" label="Modus" cols="12 6@tablet 3@desktop 2@hd">
            <option :disabled="!hasPickupLicense" value="pickup">Pick-Up</option>
            <option value="current">Absolut</option>
          </hm-input-select>
          <hm-input-select v-model="pickup.value" label="Wert" cols="12 6@tablet 3@desktop 2@hd">
            <option :disabled="!hasPickupLicense" value="availableRoomNights">Verfügbare Zimmernächte</option>
            <option :disabled="!hasPickupLicense" value="roomNights">Gebuchte Zimmernächte</option>
            <option :disabled="!hasPickupLicense" value="roomNightsDirect">Gebuchte Zimmernächte (Direct)</option>
            <option :disabled="!hasPickupLicense" value="roomNightsWeb">Gebuchte Zimmernächte (Web)</option>
            <option :disabled="!hasPickupLicense" value="roomNightsOta">Gebuchte Zimmernächte (OTA)</option>
            <option :disabled="!hasPickupLicense" value="arrivals">Zimmerankünfte</option>
            <option :disabled="!hasPickupLicense" value="bookings">Zimmerreservierungen</option>
            <option :disabled="!hasPickupLicense" value="occupancy">Auslastung (%)</option>
            <!--            <option value="revenue">Umsatz</option>-->
            <option :disabled="!hasPickupLicense" value="lodgingRevenue">Beherbergungsumsatz</option>
            <option :disabled="!hasPickupLicense" value="lodgingRevenueDirect">Beherbergungsumsatz (Direct)</option>
            <option :disabled="!hasPickupLicense" value="lodgingRevenueWeb">Beherbergungsumsatz (Web)</option>
            <option :disabled="!hasPickupLicense" value="lodgingRevenueOta">Beherbergungsumsatz (OTA)</option>
            <!--            <option value="additionalServiceRevenue">Umsatz Zusatzleistungen</option>-->
            <option :disabled="!hasPickupLicense" value="adr">ADR</option>
            <option :disabled="!hasPickupLicense" value="adrDirect">ADR (Direct)</option>
            <option :disabled="!hasPickupLicense" value="adrWeb">ADR (Web)</option>
            <option :disabled="!hasPickupLicense" value="adrOta">ADR (OTA)</option>
            <option value="revpar">RevPAR</option>
          </hm-input-select>
          <hm-input-toggle v-model="pickup.showDirectWebOta" :disabled="!hasPickupLicense" label="Direct/Web/OTA" cols="12 6@tablet 3@desktop 2@hd" />
        </hm-card-inner>
        <hm-card-inner :full-size="true" min-height="30rem">
          <spinner-overlay v-if="pickup.loading" />
          <template v-if="!isNil(pickup.data) && !isNil(pickup.groups)">
            <hm-table :columns="`${pickup.groups.length + 2}`" :hover="false" :sticky="true" ref="pickup">
              <div class="hm-table__th">Datum</div>
              <div class="hm-table__th text-right">Gesamt</div>
              <template v-for="(month, monthIdx) in pickup.groups">
                <div v-if="monthIdx < pickup.visibleGroups" :key="`head-${month}`" class="hm-table__th text-right">{{ month }}</div>
              </template>
              <template v-for="(value, key, lineIdx) in pickup.data">
                <div :key="`${key}`" class="hm-table__td text--bold">{{ key | toDate }}</div>
                <div v-if="['lodgingRevenue','lodgingRevenueDirect','lodgingRevenueWeb','lodgingRevenueOta'].includes(pickup.value)" :key="`${key}-total`" class="hm-table__td text--bold text-right">{{ pickup.mode === 'pickup' && value.totals[pickup.mode][pickup.value] >= 0 ? '+' : '' }}{{ value.totals[pickup.mode][pickup.value] | toCurrency }}&nbsp;{{ pickupUnit }}</div>
                <div v-else-if="['arrivals','roomNights','roomNightsDirect','roomNightsWeb','roomNightsOta'].includes(pickup.value)" :key="`${key}-total`" class="hm-table__td text--bold text-right">{{ pickup.mode === 'pickup' && value.totals[pickup.mode][pickup.value] >= 0 ? '+' : '' }}{{ value.totals[pickup.mode][pickup.value] | toInt }}&nbsp;{{ pickupUnit }}</div>
                <div v-else :key="`${key}-total`" class="hm-table__td text--bold text-right">&nbsp;</div>
                <template v-for="(month, monthIdx) in pickup.groups">
                  <template v-if="monthIdx < pickup.visibleGroups">
                    <template v-if="isNil(value[month])">
                      <div :key="`${key}-${month}`" class="hm-table__td text-right hm-table__td--inactive">&nbsp;</div>
                    </template>
                    <template v-else>
                      <div v-if="['adr','adrDirect','adrWeb','adrOta','revpar','lodgingRevenue','lodgingRevenueDirect','lodgingRevenueWeb','lodgingRevenueOta'].includes(pickup.value)"
                           :class="[{'blur': !hasPickupLicense && (lineIdx === monthIdx || lineIdx >= 2)}]"
                           :key="`${key}-${month}`"
                           :style="getPickupColor(value[month].hotelReport[pickup.mode][pickup.value])"
                           class="hm-table__td text-right">{{ pickup.mode === 'pickup' && value[month].hotelReport[pickup.mode][pickup.value] >= 0 ? '+' : '' }}{{ value[month].hotelReport[pickup.mode][pickup.value] | toCurrency }}&nbsp;{{ pickupUnit }}</div>
                      <div v-else-if="['occupancy'].includes(pickup.value)"
                           :class="[{'blur': !hasPickupLicense && (lineIdx === monthIdx || lineIdx >= 2)}]"
                           :key="`${key}-${month}`"
                           :style="getPickupColor(value[month].hotelReport[pickup.mode][pickup.value])"
                           class="hm-table__td text-right">{{ pickup.mode === 'pickup' && value[month].hotelReport[pickup.mode][pickup.value] >= 0 ? '+' : '' }}{{ value[month].hotelReport[pickup.mode][pickup.value] | toPercentage }}&nbsp;{{ pickupUnit }}</div>
                      <div v-else
                           :class="[{'blur': !hasPickupLicense && (lineIdx === monthIdx || lineIdx >= 2)}]"
                           :key="`${key}-${month}`"
                           :style="getPickupColor(value[month].hotelReport[pickup.mode][pickup.value])"
                           class="hm-table__td text-right">{{ pickup.mode === 'pickup' && value[month].hotelReport[pickup.mode][pickup.value] >= 0 ? '+' : '' }}{{ value[month].hotelReport[pickup.mode][pickup.value] | toInt }}&nbsp;{{ pickupUnit }}</div>
                    </template>
                  </template>
                </template>
              </template>
              <!-- Spacer -->
              <div class="hm-table__td text--bold">&nbsp;</div>
              <div class="hm-table__td text--bold">&nbsp;</div>
              <template v-for="(value, key) in pickup.monthTotals.pickup">
                <div :key="`${key}-spacer`" class="hm-table__td text-right text--bold">&nbsp;</div>
              </template>
              <!-- Totals -->
              <div class="hm-table__td text--bold">Auslastung</div>
              <div v-if="hasPickupLicense" class="hm-table__td text-right text--bold">{{ (100 / Math.max(1, sumBy(Object.values(pickup.monthTotals.pickup), 'availableRoomNights')) * sumBy(Object.values(pickup.monthTotals.pickup), 'roomNights')) | toCurrency }}&nbsp;%</div>
              <div v-else class="hm-table__td text-right text--bold blur">{{ 0 | toCurrency }}&nbsp;%</div>
              <template v-for="(value, key) in pickup.monthTotals.pickup">
                <div v-if="hasPickupLicense" :key="`${key}-total-occupancy`" class="hm-table__td text-right text--bold">{{ value.occupancy | toPercentage }}&nbsp;%</div>
                <div v-else :key="`${key}-total-occupancy`" class="hm-table__td text-right text--bold blur">{{ 0 | toPercentage }}&nbsp;%</div>
              </template>
              <div class="hm-table__td text--bold">Gebuchte Zimmernächte</div>
              <div v-if="hasPickupLicense" class="hm-table__td text-right text--bold">{{ sumBy(Object.values(pickup.monthTotals.pickup), 'roomNights') | toInt }}</div>
              <div v-else class="hm-table__td text-right text--bold blur">{{ 0 | toInt }}</div>
              <template v-for="(value, key) in pickup.monthTotals.pickup">
                <div v-if="hasPickupLicense" :key="`${key}-total-roomNights`" class="hm-table__td text-right text--bold">{{ value.roomNights | toInt }}</div>
                <div v-else :key="`${key}-total-roomNights`" class="hm-table__td text-right text--bold blur">{{ 0 | toInt }}</div>
              </template>
              <template v-if="hasPickupLicense && pickup.showDirectWebOta">
                <div class="hm-table__td text--bold">Gebuchte Zimmernächte (Direct)</div>
                <div class="hm-table__td text-right text--bold">{{ sumBy(Object.values(pickup.monthTotals.pickup), 'roomNightsDirect') | toInt }}</div>
                <template v-for="(value, key) in pickup.monthTotals.pickup">
                  <div :key="`${key}-total-roomNightsDirect`" class="hm-table__td text-right text--bold">{{ value.roomNightsDirect | toInt }}</div>
                </template>
                <div class="hm-table__td text--bold">Gebuchte Zimmernächte (Web)</div>
                <div class="hm-table__td text-right text--bold">{{ sumBy(Object.values(pickup.monthTotals.pickup), 'roomNightsWeb') | toInt }}</div>
                <template v-for="(value, key) in pickup.monthTotals.pickup">
                  <div :key="`${key}-total-roomNightsWeb`" class="hm-table__td text-right text--bold">{{ value.roomNightsWeb | toInt }}</div>
                </template>
                <div class="hm-table__td text--bold">Gebuchte Zimmernächte (OTA)</div>
                <div class="hm-table__td text-right text--bold">{{ sumBy(Object.values(pickup.monthTotals.pickup), 'roomNightsOta') | toInt }}</div>
                <template v-for="(value, key) in pickup.monthTotals.pickup">
                  <div :key="`${key}-total-roomNightsOta`" class="hm-table__td text-right text--bold">{{ value.roomNightsOta | toInt }}</div>
                </template>
              </template>
              <div class="hm-table__td text--bold">Beherbergungsumsatz</div>
              <div v-if="hasPickupLicense" class="hm-table__td text-right text--bold">{{ sumBy(Object.values(pickup.monthTotals.pickup), 'lodgingRevenue') | toCurrency }}&nbsp;{{ hotelSettings.details.currency }}</div>
              <div v-else class="hm-table__td text-right text--bold blur">{{ 0 | toCurrency }}&nbsp;{{ hotelSettings.details.currency }}</div>
              <template v-for="(value, key) in pickup.monthTotals.pickup">
                <div v-if="hasPickupLicense" :key="`${key}-total-lodgingRevenue`" class="hm-table__td text-right text--bold">{{ value.lodgingRevenue | toCurrency }}&nbsp;{{ hotelSettings.details.currency }}</div>
                <div v-else :key="`${key}-total-lodgingRevenue`" class="hm-table__td text-right text--bold blur">{{ 0 | toCurrency }}&nbsp;{{ hotelSettings.details.currency }}</div>
              </template>
              <template v-if="hasPickupLicense && pickup.showDirectWebOta">
                <div class="hm-table__td text--bold">Beherbergungsumsatz (Direct)</div>
                <div class="hm-table__td text-right text--bold">{{ sumBy(Object.values(pickup.monthTotals.pickup), 'lodgingRevenueDirect') | toCurrency }}&nbsp;{{ hotelSettings.details.currency }}</div>
                <template v-for="(value, key) in pickup.monthTotals.pickup">
                  <div :key="`${key}-total-lodgingRevenueDirect`" class="hm-table__td text-right text--bold">{{ value.lodgingRevenueDirect | toCurrency }}&nbsp;{{ hotelSettings.details.currency }}</div>
                </template>
                <div class="hm-table__td text--bold">Beherbergungsumsatz (Web)</div>
                <div class="hm-table__td text-right text--bold">{{ sumBy(Object.values(pickup.monthTotals.pickup), 'lodgingRevenueWeb') | toCurrency }}&nbsp;{{ hotelSettings.details.currency }}</div>
                <template v-for="(value, key) in pickup.monthTotals.pickup">
                  <div :key="`${key}-total-lodgingRevenueWeb`" class="hm-table__td text-right text--bold">{{ value.lodgingRevenueWeb | toCurrency }}&nbsp;{{ hotelSettings.details.currency }}</div>
                </template>
                <div class="hm-table__td text--bold">Beherbergungsumsatz (OTA)</div>
                <div class="hm-table__td text-right text--bold">{{ sumBy(Object.values(pickup.monthTotals.pickup), 'lodgingRevenueOta') | toCurrency }}&nbsp;{{ hotelSettings.details.currency }}</div>
                <template v-for="(value, key) in pickup.monthTotals.pickup">
                  <div :key="`${key}-total-lodgingRevenueOta`" class="hm-table__td text-right text--bold">{{ value.lodgingRevenueOta | toCurrency }}&nbsp;{{ hotelSettings.details.currency }}</div>
                </template>
              </template>
              <div class="hm-table__td text--bold">RevPAR</div>
              <div v-if="hasPickupLicense" class="hm-table__td text-right text--bold">{{ (sumBy(Object.values(pickup.monthTotals.pickup), 'lodgingRevenue') / Math.max(1, sumBy(Object.values(pickup.monthTotals.pickup), 'availableRoomNights'))) | toCurrency }}&nbsp;{{ hotelSettings.details.currency }}</div>
              <div v-else class="hm-table__td text-right text--bold blur">{{ 0 | toCurrency }}&nbsp;{{ hotelSettings.details.currency }}</div>
              <template v-for="(value, key) in pickup.monthTotals.pickup">
                <div v-if="hasPickupLicense" :key="`${key}-total-revpar`" class="hm-table__td text-right text--bold">{{ value.revpar | toCurrency }}&nbsp;{{ hotelSettings.details.currency }}</div>
                <div v-else :key="`${key}-total-revpar`" class="hm-table__td text-right text--bold blur">{{ 0 | toCurrency }}&nbsp;{{ hotelSettings.details.currency }}</div>
              </template>
              <div class="hm-table__td text--bold">ADR</div>
              <div v-if="hasPickupLicense" class="hm-table__td text-right text--bold">{{ (sumBy(Object.values(pickup.monthTotals.pickup), 'lodgingRevenue') / Math.max(1, sumBy(Object.values(pickup.monthTotals.pickup), 'roomNights'))) | toCurrency }}&nbsp;{{ hotelSettings.details.currency }}</div>
              <div v-else class="hm-table__td text-right text--bold blur">{{ 0 | toCurrency }}&nbsp;{{ hotelSettings.details.currency }}</div>
              <template v-for="(value, key) in pickup.monthTotals.pickup">
                <div v-if="hasPickupLicense" :key="`${key}-total-adr`" class="hm-table__td text-right text--bold">{{ value.adr | toCurrency }}&nbsp;{{ hotelSettings.details.currency }}</div>
                <div v-else :key="`${key}-total-adr`" class="hm-table__td text-right text--bold blur">{{ 0 | toCurrency }}&nbsp;{{ hotelSettings.details.currency }}</div>
              </template>
              <template v-if="hasPickupLicense && pickup.showDirectWebOta">
                <div class="hm-table__td text--bold">ADR (Direct)</div>
                <div class="hm-table__td text-right text--bold">{{ (sumBy(Object.values(pickup.monthTotals.pickup), 'lodgingRevenueDirect') / Math.max(1, sumBy(Object.values(pickup.monthTotals.pickup), 'roomNightsDirect'))) | toCurrency }}&nbsp;{{ hotelSettings.details.currency }}</div>
                <template v-for="(value, key) in pickup.monthTotals.pickup">
                  <div :key="`${key}-total-adrDirect`" class="hm-table__td text-right text--bold">{{ value.adrDirect | toCurrency }}&nbsp;{{ hotelSettings.details.currency }}</div>
                </template>
                <div class="hm-table__td text--bold">ADR (Web)</div>
                <div class="hm-table__td text-right text--bold">{{ (sumBy(Object.values(pickup.monthTotals.pickup), 'lodgingRevenueWeb') / Math.max(1, sumBy(Object.values(pickup.monthTotals.pickup), 'roomNightsWeb'))) | toCurrency }}&nbsp;{{ hotelSettings.details.currency }}</div>
                <template v-for="(value, key) in pickup.monthTotals.pickup">
                  <div :key="`${key}-total-adrWeb`" class="hm-table__td text-right text--bold">{{ value.adrWeb | toCurrency }}&nbsp;{{ hotelSettings.details.currency }}</div>
                </template>
                <div class="hm-table__td text--bold">ADR (OTA)</div>
                <div class="hm-table__td text-right text--bold">{{ (sumBy(Object.values(pickup.monthTotals.pickup), 'lodgingRevenueOta') / Math.max(1, sumBy(Object.values(pickup.monthTotals.pickup), 'roomNightsOta'))) | toCurrency }}&nbsp;{{ hotelSettings.details.currency }}</div>
                <template v-for="(value, key) in pickup.monthTotals.pickup">
                  <div :key="`${key}-total-adrOta`" class="hm-table__td text-right text--bold">{{ value.adrOta | toCurrency }}&nbsp;{{ hotelSettings.details.currency }}</div>
                </template>
              </template>
              <div class="hm-table__td text--bold">Ø Aufenthaltsdauer</div>
              <div v-if="hasPickupLicense" class="hm-table__td text-right text--bold">{{ (sumBy(Object.values(pickup.monthTotals.pickup), 'roomNights') / sumBy(Object.values(pickup.monthTotals.pickup), 'arrivals')) | toPercentage }}</div>
              <div v-else class="hm-table__td text-right text--bold blur">{{ 0 | toPercentage }}</div>
              <template v-for="(value, key) in pickup.monthTotals.pickup">
                <div v-if="hasPickupLicense" :key="`${key}-total-avg-stay-length`" class="hm-table__td text-right text--bold">{{ (value.roomNights / value.bookings) | toPercentage }}</div>
                <div v-else :key="`${key}-total-avg-stay-length`" class="hm-table__td text-right text--bold blur">{{ 0 | toPercentage }}</div>
              </template>
              <div class="hm-table__td text--bold">Anreisen</div>
              <div v-if="hasPickupLicense" class="hm-table__td text-right text--bold">{{ sumBy(Object.values(pickup.monthTotals.pickup), 'arrivals') | toInt }}</div>
              <div v-else class="hm-table__td text-right text--bold blur">{{ 0 | toInt }}</div>
              <template v-for="(value, key) in pickup.monthTotals.pickup">
                <div v-if="hasPickupLicense" :key="`${key}-total-arrivals`" class="hm-table__td text-right text--bold">{{ value.arrivals | toInt }}</div>
                <div v-else :key="`${key}-total-arrivals`" class="hm-table__td text-right text--bold blur">{{ 0 | toInt }}</div>
              </template>
            </hm-table>
          </template>
        </hm-card-inner>
      </tabable-card-content>
    </tabable-card>

    <hm-floating-buttons>
      <hm-floating-button v-if="(hasPickupLicense || hasRole('ROLE_SYSADMIN')) && activeCardIndex === 2"
                          label="In Zwischenablage kopieren"
                          icon="copy"
                          color="orange"
                          @click="copyPickup" />
    </hm-floating-buttons>
  </main>
</template>

<script>
/* eslint func-names: 0 */

import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import {
  isArray,
  join,
  isNil,
  isEmpty,
  groupBy,
  values,
  head,
  cloneDeep,
  concat,
  unionBy,
  reverse,
  debounce,
  sum,
  isNumber, uniq, orderBy, padStart, take, xor, remove, findLastIndex, sumBy, pullAt,
} from 'lodash';
import HmCardInnerLine from '../lib/layout/HmCardInnerLine';
import HmCardInner from '../lib/layout/HmCardInner';
import uuid from 'uuid';
import HmCol from '../lib/layout/HmCol';
import HmInput from '../lib/inputs/HmInput';
import ContenteditableDiv from '../lib/util/ContenteditableDiv';
import HmInputSelect from '../lib/inputs/HmInputSelect';
import TabableCard from '../lib/layout/TabableCard.vue';
import TabableCardContent from '../lib/layout/TabableCardContent.vue';
import HmTable from '../lib/table/HmTable.vue';
import percentile from 'percentile';
import SpinnerOverlay from '../lib/util/SpinnerOverlay.vue';
import { TimelineMax, TweenMax } from 'gsap';

export default {
  name: 'PageCockpit',
  components: { SpinnerOverlay, HmTable, TabableCardContent, TabableCard, HmInputSelect, ContenteditableDiv, HmCardInner, HmInput, HmCol },

  filters: {
    round(value) {
      return Math.round(value);
    },
    toPercentage: function (value) {
      return `${(isNil(value) ? 0 : value).toLocaleString(
        window.i18n.locale,
        {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        },
      )}`;
    },
  },
  mixins: [HotelmeisterMixin],


  data() {
    return {
      startDate: null,
      endDate: null,
      debouncedUpdateData: null,
      roomTypeAvailability: {},
      lastYearRoomTypeAvailability: {},
      expandable: true,
      comparisonYearOffset: -1,
      pickup: {
        loading: false,
        showDirectWebOta: false,
        debouncedUpdateData: null,
        visibleGroups: 12,
        granularity: 'Monthly',
        startDate: this.$moment().add(-1, 'years').format('YYYYMMDD'),
        mode: 'current', // pickup, current
        value: 'revpar',
        data: null,
        groups: null,
        range: {
          current: null,
          pickup: null,
        },
        percentile: {
          current: null,
          pickup: null,
        },
        monthTotals: {
          current: null,
          pickup: null,
        },
      },
      shortTerm: {
        loading: false,
        pickupDays: 1,
        debouncedUpdateData: null,
        visibleGroups: 30,
        granularity: 'Daily',
        startDate: this.$moment().add(-7, 'days').format('YYYYMMDD'), // -1 is required for pickup to be calculated
        mode: 'current', // pickup, current
        value: 'revpar',
        data: null,
        groups: null,
        range: {
          current: null,
          pickup: null,
        },
        percentile: {
          current: null,
          pickup: null,
        },
        monthTotals: {
          current: null,
          pickup: null,
        },
      },
      shortTermPreviousYear: {
        loading: false,
        debouncedUpdateData: null,
        visibleGroups: 30,
        granularity: 'Daily',
        startDate: this.$moment().add(-1, 'years').format('YYYYMMDD'),
        mode: 'current', // pickup, current
        value: 'revpar',
        data: null,
        groups: null,
        range: {
          current: null,
          pickup: null,
        },
        percentile: {
          current: null,
          pickup: null,
        },
        monthTotals: {
          current: null,
          pickup: null,
        },
      },
      activeCardIndex: 0,
    };
  },

  computed: {
    hasPickupLicense() {
      if (this.hasLicense('PickupReporting') || this.hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')) return true;
      else return this.hasRole('ROLE_SYSADMIN');
    },
    today: function () {
      return this.$moment().format('YYYYMMDD');
    },
    pickupUnit: function () {
      switch (this.pickup.value) {
        case 'occupancy':
          return '%';
        case 'adr':
        case 'revpar':
        case 'revenue':
        case 'lodgingRevenue':
        case 'lodgingRevenueDirect':
        case 'lodgingRevenueWeb':
        case 'lodgingRevenueOta':
        case 'additionalServiceRevenue':
          return this.hotelSettings.details.currency;
        default:
          return '';
      }
    },
    days: function () {
      if (isNil(this.startDate) || isNil(this.endDate)) return 14;

      return Math.round(Math.abs(this.$moment.duration(this.$moment(this.endDate, 'YYYYMMDD').diff(this.$moment(this.startDate, 'YYYYMMDD'))).asDays()));
    },
    averageOccupancy: function () {
      if (isEmpty(this.roomTypeAvailability) || isNil(this.roomTypeAvailability.Total)) return 0;

      return sum(Object.values(this.roomTypeAvailability.Total).map(it => (it.inventory === 0 ? 0 : (100 / it.inventory * (it.stays))))) / Object.values(this.roomTypeAvailability.Total).length;
    },
    lastYearAverageOccupancy: function () {
      if (isEmpty(this.lastYearRoomTypeAvailability) || isNil(this.lastYearRoomTypeAvailability.Total)) return 0;

      return sum(Object.values(this.lastYearRoomTypeAvailability.Total).map(it => (it.inventory === 0 ? 0 : (100 / it.inventory * (it.stays))))) / Object.values(this.lastYearRoomTypeAvailability.Total).length;
    },
    chartData: function () {
      if (isEmpty(this.roomTypeAvailability) || isNil(this.roomTypeAvailability.Total)) return [];

      let data = Object.entries(this.roomTypeAvailability.Total).map(it => ({ date: it[0], ...it[1] }));
      data = data.map(it => [this.$moment(it.date, 'YYYYMMDD').toDate(), it.stays, it.arrivals || 0, it.departures || 0, it.inventory]);

      return concat([[this.$t('pageCockpit.chart.date'), this.$t('pageCockpit.chart.occupied'), this.$t('pageCockpit.chart.arrivals'), this.$t('pageCockpit.chart.departures'), this.$t('pageCockpit.chart.totalRoomsAvailable')]], data);
    },
    maxInventory: function () {
      if (isEmpty(this.roomTypeAvailability) || isNil(this.roomTypeAvailability.Total)) return 0;

      return Math.max(...Object.values(this.roomTypeAvailability.Total).map(it => it.inventory));
    },
    occupancyGaugeData: function () {
      const ret = [
        ['Label', 'Value'],
        [this.$t('pageCockpit.chart.occupancyRate'), { v: this.averageOccupancy, f: `${Math.round(this.averageOccupancy * 10) / 10}%` }],
      ];

      if (isNumber(this.lastYearAverageOccupancy) && !Number.isNaN(this.lastYearAverageOccupancy)) {
        ret.push([this.$t('pageCockpit.chart.previousYear'), { v: this.lastYearAverageOccupancy, f: `${Math.round(this.lastYearAverageOccupancy * 10) / 10}%` }]);
      }

      return ret;
    },
    occupancyGaugeChartOptions: function () {
      return {
        title: this.$t('pageCockpit.chart.occupancyRate'),
        fontName: 'Nunito Sans',
        fontSize: 13,
        titleTextStyle: { color: '#00272e' },
        greenColor: '#2da295',
        yellowColor: '#f69e4d',
        redColor: '#d54c4d',
        greenFrom: 75,
        greenTo: 100,
        yellowFrom: 35,
        yellowTo: 55,
        redFrom: 0,
        redTo: 35,
        majorTicks: [],
        chartArea: {
          top: 60,
          height: '50%',
        },
        backgroundColor: {
          fill: 'transparent',
        },
        height: 250,
      };
    },
    occupancyChartData: function () {
      if (isEmpty(this.roomTypeAvailability) || isNil(this.roomTypeAvailability.Total)
          || isEmpty(this.lastYearRoomTypeAvailability) || isNil(this.lastYearRoomTypeAvailability.Total)) return [];

      if (this.lastYearRoomTypeAvailability.Total.length !== this.roomTypeAvailability.Total.length) return [];

      const currentYearData = Object.entries(this.roomTypeAvailability.Total).map(it => ({ date: it[0], ...it[1] }));
      const lastYearData = Object.entries(this.lastYearRoomTypeAvailability.Total).map(it => ({ date: it[0], ...it[1] }));

      if (isEmpty(lastYearData)) {
        currentYearData.forEach((it) => {
          it.occupancy = it.inventory === 0 ? 0 : (100 / it.inventory * (it.stays));
        });

        let data = currentYearData.map(it => ({
          currentYear: it,
        }));

        data = data.map(it => [
          this.$moment(it.currentYear.date, 'YYYYMMDD')
            .format(this.dayMonthFormat),
          {
            v: it.currentYear.occupancy,
            f: `${Math.round(it.currentYear.occupancy)}%`,
          },
        ]);
        return concat([[this.$t('pageCockpit.chart.date'), this.$t('pageCockpit.chart.currentYear')]], data);
      } else {
        currentYearData.forEach((it) => {
          it.occupancy = it.inventory === 0 ? 0 : (100 / it.inventory * (it.stays));
        });
        lastYearData.forEach((it) => {
          it.occupancy = 100 / it.inventory * (it.stays);
        });

        let data = currentYearData.map(it => ({
          currentYear: it,
          lastYear: { occupancy: 0 },
        }));

        if (data.length !== lastYearData.length) return [];

        lastYearData.forEach((it, idx) => {
          data[idx].lastYear = it;
        });

        data = data.map(it => [
          this.$moment(it.currentYear.date, 'YYYYMMDD')
            .format(this.dayMonthFormat),
          {
            v: it.currentYear.occupancy,
            f: `${Math.round(it.currentYear.occupancy)}%`,
          },
          {
            v: it.lastYear.occupancy,
            f: `${Math.round(it.lastYear.occupancy)}%`,
          },
        ]);

        return concat([[this.$t('pageCockpit.chart.date'), this.$t('pageCockpit.chart.currentYear'), this.$t('pageCockpit.chart.previousYear')]], data);
      }
    },
    occupancyChartOptions: function () {
      return {
        title: this.$t('pageCockpit.chart.occupancyRate'),
        fontName: 'Nunito Sans',
        fontSize: 13,
        // curveType: 'function',
        curveType: 'none',
        titleTextStyle: { color: '#00272e' },
        legend: {
          position: 'top',
          alignment: 'center',
          textStyle: { color: '#00272e' },
          pagingTextStyle: { color: '#7a7f7e' },
          scrollArrows: {
            activeColor: '#2da295',
            inactiveColor: '#eceeed',
          },
        },
        pointSize: 0,
        lineWidth: 2,
        series: {
          0: {
            targetAxisIndex: 0,
            color: '#2da295',
          },
          1: {
            targetAxisIndex: 0,
            color: '#d54c4d',
          },
        },
        vAxes: {
          0: {
            title: this.$t('pageCockpit.chart.occupancyRatePercentage'),
            viewWindow: {
              min: 0,
              max: 120,
            },
            gridlines: {
              count: 4,
            },
            minorGridlines: {
              count: 1,
              color: 'transparent',
            },
          },
        },
        vAxis: {
          titleTextStyle: { color: '#00272e', italic: false },
          textStyle: { color: '#00272e' },
        },
        hAxis: {
          titleTextStyle: { color: '#00272e', italic: false },
          textStyle: { color: '#00272e' },
          showTextEvery: 2,
          format: 'd.M.',
          gridlines: {
            color: 'transparent',
            count: this.days,
          },
          slantedText: true,
          slantedTextAngle: 45,
        },
        chartArea: {
          top: 60,
          height: '50%',
        },
        backgroundColor: {
          fill: 'transparent',
        },
        height: 300,
      };
    },
    chartOptions: function () {
      return {
        title: this.$t('pageCockpit.chart.arrivalsDepartures'),
        fontName: 'Nunito Sans',
        fontSize: 13,
        // curveType: 'function',
        curveType: 'none',
        titleTextStyle: { color: '#00272e' },
        legend: {
          position: 'top',
          alignment: 'center',
          textStyle: { color: '#00272e' },
          pagingTextStyle: { color: '#7a7f7e' },
          scrollArrows: {
            activeColor: '#2da295',
            inactiveColor: '#eceeed',
          },
        },
        pointSize: 0,
        lineWidth: 2,
        series: {
          0: {
            targetAxisIndex: 0,
            color: '#00272e',
          },
          1: {
            targetAxisIndex: 0,
            color: '#2da295',
          },
          2: {
            targetAxisIndex: 0,
            color: '#d54c4d',
          },
          3: {
            targetAxisIndex: 0,
            color: '#63a0d3',
          },
        },
        vAxes: {
          0: {
            title: this.$t('pageCockpit.chart.rooms'),
            viewWindow: {
              min: 0,
              max: this.maxInventory + 4,
            },
            gridlines: {
              // color: 'transparent',
              count: Math.round(this.maxInventory / 4),
            },
            minorGridlines: {
              count: 1,
              color: 'transparent',
            },
          },
        },
        vAxis: {
          titleTextStyle: { color: '#00272e', italic: false },
          textStyle: { color: '#00272e' },
        },
        hAxis: {
          titleTextStyle: { color: '#00272e', italic: false },
          textStyle: { color: '#00272e' },
          showTextEvery: 2,
          format: 'd.M.yyyy',
          gridlines: {
            color: 'transparent',
            count: this.days,
          },
          slantedText: true,
          slantedTextAngle: 45,
        },
        chartArea: {
          top: 60,
          height: '50%',
        },
        backgroundColor: {
          fill: 'transparent',
        },
        height: 300,
      };
    },
  },

  watch: {
    days: function () {
      this.debouncedUpdateData();
    },
    startDate: function () {
      this.debouncedUpdateData();
    },
    endDate: function () {
      this.debouncedUpdateData();
    },
    comparisonYearOffset: function () {
      this.debouncedUpdateData();
    },
    'pickup.startDate': function () {
      if (!this.hasPickupLicense) return;
      this.pickup.debouncedUpdateData();
    },
    'pickup.visibleGroups': function () {
      if (!this.hasPickupLicense) return;
      this.pickup.debouncedUpdateData();
    },
    'pickup.granularity': function () {
      if (!this.hasPickupLicense) return;
      this.pickup.debouncedUpdateData();
    },
    'pickup.value': function () {
      if (!this.hasPickupLicense) {
        this.pickup.value = 'revpar';
      } else {
        this.pickup.debouncedUpdateData();
      }
    },
    'shortTerm.pickupDays': function () {
      if (!this.hasPickupLicense) return;
      this.shortTerm.debouncedUpdateData();
    },
    activeCardIndex: function (newVal) {
      if (newVal === 0) {
        this.$nextTick(() => {
          this.updateChart('chart1');
          this.updateChart('chart2');
        });
      }
    },
  },

  created: function () {
    this.$serviceBus.$on('shortcut.pageRight', this.pageRight);
    this.$serviceBus.$on('shortcut.pageLeft', this.pageLeft);
    this.$serviceBus.$on('shortcut.pageUp', this.pageRightYear);
    this.$serviceBus.$on('shortcut.pageDown', this.pageLeftYear);
    this.$serviceBus.$on('shortcut.scrollTop', this.gotoToday);
    this.startDate = this.$moment().add(-2, 'days').format('YYYYMMDD');
    this.endDate = this.$moment(this.startDate, 'YYYYMMDD').add(14, 'days').format('YYYYMMDD');
    this.debouncedUpdateData = debounce(this.updateData, 1000);
    this.pickup.debouncedUpdateData = debounce(this.loadPickup, 1000);
    this.shortTerm.debouncedUpdateData = debounce(this.loadShortTerm, 1000);
    this.updateData();
  },

  beforeDestroy: function () {
    this.$serviceBus.$off('shortcut.pageRight', this.pageRight);
    this.$serviceBus.$off('shortcut.pageLeft', this.pageLeft);
    this.$serviceBus.$off('shortcut.scrollTop', this.gotoToday);
    this.$serviceBus.$off('shortcut.pageUp', this.pageRightYear);
    this.$serviceBus.$off('shortcut.pageDown', this.pageLeftYear);
  },

  methods: {
    isNil,
    pageRight() {
      if (this.activeCardIndex === 2) this.pickup.startDate = this.$moment(this.pickup.startDate, 'YYYYMMDD').add(1, 'months').format('YYYYMMDD');
    },
    pageLeft() {
      if (this.activeCardIndex === 2) this.pickup.startDate = this.$moment(this.pickup.startDate, 'YYYYMMDD').add(-1, 'months').format('YYYYMMDD');
    },
    pageRightYear() {
      if (this.activeCardIndex === 2) this.pickup.startDate = this.$moment(this.pickup.startDate, 'YYYYMMDD').add(1, 'year').format('YYYYMMDD');
    },
    pageLeftYear() {
      if (this.activeCardIndex === 2) this.pickup.startDate = this.$moment(this.pickup.startDate, 'YYYYMMDD').add(-1, 'year').format('YYYYMMDD');
    },
    gotoToday() {
      if (this.activeCardIndex === 2) this.pickup.startDate = this.$moment().add(-1, 'year').format('YYYYMMDD');
    },
    intArray: function (value, offset = 0) {
      return [...Array(value).keys()].map(x => x + offset);
    },
    updateChart(chartName) {
      const chart = this.$refs[chartName];
      if (!isNil(chart)) chart.drawChart();
    },
    updateData() {
      this.updateAvailability();
      this.updateLastYearAvailability();
    },
    updateAvailability() {
      const startDate = this.startDate;

      this.axios.get(this.serviceUrls.roomTypePlan(this), {
        params: {
          start: startDate,
          limit: this.days,
          physicalRooms: 1
        },
      }).then((response) => {
        this.roomTypeAvailability = response.data.data;
      });
    },
    updateLastYearAvailability() {
      const startDate = this.$moment(this.startDate, 'YYYYMMDD').add(this.comparisonYearOffset, 'years').format('YYYYMMDD');

      this.axios.get(this.serviceUrls.roomTypePlan(this), {
        params: {
          start: startDate,
          limit: this.days,
          physicalRooms: 1
        },
      }).then((response) => {
        this.lastYearRoomTypeAvailability = response.data.data;
      });
    },
    formatDateRange(offset) {
      const startDate = this.$moment(this.startDate, 'YYYYMMDD').add(offset, 'years').format('YYYYMMDD');
      const endDate = this.$moment(this.endDate, 'YYYYMMDD').add(offset, 'years').format('YYYYMMDD');

      return `${this.$options.filters.toDate(startDate)} - ${this.$options.filters.toDate(endDate)}`;
    },
    loadPickup() {
      if (this.pickup.loading) return;
      this.pickup.loading = true;
      this.axios.get(this.serviceUrls.cockpit.pickup(this, this.pickup.granularity), {
        params: {},
      })
        .then((response) => {
          this.parsePickupData('pickup', response.data.data);
        })
        .finally(() => {
          this.pickup.loading = false;
        });
    },
    loadShortTerm() {
      if (this.shortTerm.loading || this.shortTermPreviousYear.loading) return;
      this.shortTerm.loading = true;
      this.shortTermPreviousYear.loading = true;
      this.axios.get(this.serviceUrls.cockpit.shortTermPickup(this), {
        params: {
        },
      }).then((response) => {
        this.parsePickupData('shortTerm', response.data.data, this.shortTerm.pickupDays);
      }).finally(() => {
        this.shortTerm.loading = false;
      });
      this.axios.get(this.serviceUrls.cockpit.shortTermPreviousYearPickup(this), {
        params: {
        },
      }).then((response) => {
        this.parsePickupData('shortTermPreviousYear', response.data.data);
      }).finally(() => {
        this.shortTermPreviousYear.loading = false;
      });
    },
    parsePickupData(variableName, pickupData, pickupPeriods = 1) {
      const tmp = pickupData;
      const lines = Object.keys(tmp);
      const ret = Object.fromEntries(
        lines.map(it => [
          it,
          Object.fromEntries(
            tmp[it].map((data) => {
              const date = data.date;
              delete data.hotelReport.roomTypeDetails;
              delete data.hotelId;
              delete data.date;
              delete data.hotelReport.hotelId;
              data.hotelReport = {
                current: data.hotelReport,
                previous: null,
                pickup: null,
              };
              if (!isNil(data.hotelReport.current)) {
                data.hotelReport.current.occupancy = data.hotelReport.current.availableRoomNights === 0 ? 0 : 100 / data.hotelReport.current.availableRoomNights * data.hotelReport.current.roomNights;
              }
              return [`${date}`, data];
            }),
          ),
        ]),
      );

      Object.keys(ret).forEach((it) => {
        if (it < this[variableName].startDate) {
          delete ret[it];
          remove(lines, n => n === it);
        }
      });

      const rangeCurrent = {};
      const rangePickup = {};

      const previousLines = [];
      let previousLine = null;
      for (let i = 0; i < lines.length; i++) {
        if (previousLines.length === pickupPeriods) {
          previousLine = pullAt(previousLines, 0)[0];
        }

        const key = lines[i];
        const value = ret[key];
        // eslint-disable-next-line no-loop-func
        Object.keys(value).forEach((month) => {
          if (isNil(previousLine) || isNil(previousLine[month])) {
            value[month].hotelReport.pickup = value[month].hotelReport.current;
          } else {
            value[month].hotelReport.previous = previousLine[month].hotelReport.current;
            value[month].hotelReport.pickup = {};

            Object.keys(value[month].hotelReport.current).forEach((valKey) => {
              if (isEmpty(value[month].hotelReport.previous) || isNil(value[month].hotelReport.previous[valKey])) {
                value[month].hotelReport.pickup[valKey] = value[month].hotelReport.current[valKey];
              } else {
                value[month].hotelReport.pickup[valKey] = value[month].hotelReport.current[valKey] - value[month].hotelReport.previous[valKey];
              }
            });
          }

          Object.keys(value[month].hotelReport.current).forEach((valKey) => {
            if (isNil(rangeCurrent[valKey])) rangeCurrent[valKey] = [];
            rangeCurrent[valKey].push(value[month].hotelReport.current[valKey]);
            if (isNil(rangePickup[valKey])) rangePickup[valKey] = [];
            rangePickup[valKey].push(value[month].hotelReport.pickup[valKey]);
          });
        });

        previousLines.push(value);
        // previousLine = value;
      }

      this[variableName].range.current = rangeCurrent;
      this[variableName].range.pickup = rangePickup;

      const percentileCurrent = {};
      const percentilePickup = {};

      Object.keys(this[variableName].range.current).forEach((key) => {
        percentileCurrent[key] = percentile(
          [1, 4, 7, 10, 14, 17, 20, 23, 26, 29, 33, 36, 39, 42, 45, 48, 52, 55, 58, 61, 64, 67, 71, 74, 77, 80, 83, 86, 90, 93, 96, 99],
          this[variableName].range.current[key],
        );
        percentilePickup[key] = percentile(
          [1, 4, 7, 10, 14, 17, 20, 23, 26, 29, 33, 36, 39, 42, 45, 48, 52, 55, 58, 61, 64, 67, 71, 74, 77, 80, 83, 86, 90, 93, 96, 99],
          this[variableName].range.pickup[key],
        );
      });

      this[variableName].percentile.current = percentileCurrent;
      this[variableName].percentile.pickup = percentilePickup;

      this[variableName].groups = take(orderBy(uniq(Object.values(ret).flatMap(it => Object.keys(it)))), this[variableName].visibleGroups);

      Object.values(ret).forEach((it) => {
        xor(Object.keys(it), this[variableName].groups).forEach((key) => { delete it[key]; });
      });

      Object.keys(ret).forEach((it) => {
        if (isEmpty(ret[it])) delete ret[it];
      });

      Object.values(ret).forEach((value) => {
        value.totals = {
          current: {},
          pickup: {},
        };
        this[variableName].groups.forEach((month) => {
          if (!isNil(value[month])) {
            Object.keys(value[month].hotelReport.current).forEach((valKey) => {
              if (isNil(value.totals.current[valKey])) value.totals.current[valKey] = 0.0;
              value.totals.current[valKey] += value[month].hotelReport.current[valKey];
              if (isNil(value.totals.pickup[valKey])) value.totals.pickup[valKey] = 0.0;
              value.totals.pickup[valKey] += value[month].hotelReport.pickup[valKey];
            });
          }
        });
      });

      const monthTotals = {
        current: { },
        pickup: { },
      };

      this[variableName].groups.forEach((month) => {
        if (isNil(monthTotals.current[month])) monthTotals.current[month] = {};
        if (isNil(monthTotals.pickup[month])) monthTotals.pickup[month] = {};
        const monthValues = Object.values(ret).map(value => value[month]).filter(it => !isNil(it));
        monthValues.forEach((it) => {
          Object.keys(it.hotelReport.current).forEach((valKey) => {
            if (isNil(monthTotals.current[month][valKey])) monthTotals.current[month][valKey] = 0.0;
            monthTotals.current[month][valKey] += it.hotelReport.current[valKey];
            if (isNil(monthTotals.pickup[month][valKey])) monthTotals.pickup[month][valKey] = 0.0;
            monthTotals.pickup[month][valKey] += it.hotelReport.pickup[valKey];
          });
        });
      });

      this[variableName].monthTotals = monthTotals;

      this[variableName].data = ret;
    },
    getPickupColor(value) {
      const i = Math.max(0, findLastIndex(this.pickup.percentile[this.pickup.mode][this.pickup.value], p => p < value));
      const h = i * 3.75; // we need hue values from 0 to 120
      return {
        background: `hsla(${h}, 100%, 50%, 0.75)`,
      };
    },
    isWeekend(date) {
      switch (new Date(date).getDay()) {
        case 0: // Sunday
        case 1: // Monday
        case 2: // Tuesday
        case 3: // Wednesday
        case 4: // Thursday
          return false;
        case 5: // Friday
        case 6: // Saturday
          return true;
        default:
          return false;
      }
    },
    shortTermForecast(date, value, key, maxValue = -1) {
      if (isNil(this.shortTermPreviousYear.data)) return 'N/A';
      try {
        const previousYear = this.$moment(date).add(-1, 'years').format('YYYY-MM-DD');
        const previousYearYMD = this.$moment(date).add(-1, 'years').format('YYYYMMDD');
        // console.log(this.shortTermPreviousYear.data[this.shortTermPreviousYear.startDate][previousYear].hotelReport.current[key]);
        const valueToday1Y = this.shortTermPreviousYear.data[this.shortTermPreviousYear.startDate][previousYear].hotelReport.current[key];
        const valueAtDate = this.shortTermPreviousYear.data[previousYearYMD][previousYear].hotelReport.current[key];
        const diff = valueAtDate - valueToday1Y;
        // console.log('===========================');
        // console.log(value);
        // console.log(`+ ${diff}`);
        // console.log(`MAX: ${maxValue}`);
        const forecast = maxValue >= 0 ? Math.max(value, Math.min(value + diff, maxValue)) : value + diff;
        const effectiveDiff = forecast - value;
        // console.log(`= ${forecast}`);
        return `${forecast.toFixed(0)} (${diff >= 0 ? '+' : ''}${effectiveDiff})`;
      } catch (e) {
        return 'N/A';
      }
    },
    sumBy: sumBy,
    copyPickup() {
      const el = this.$refs.pickup.$el;
      if (!el) throw new Error("Table not found");

      function clean(node) {
        var t = (node && node.textContent) ? node.textContent : "";
        return t
          .replace(/\u00A0/g, " ")   // &nbsp;
          .replace(/\s+/g, " ")
          .replace(/^\s+|\s+$/g, ""); // trim
      }

      function toArray(nodeList) {
        return Array.prototype.slice.call(nodeList || []);
      }

      var style = window.getComputedStyle ? window.getComputedStyle(el) : null;
      var cols = 0;
      if (style && style.getPropertyValue) {
        cols = parseInt(style.getPropertyValue("--table-columns"), 10) || 0;
      }
      if (!cols) cols = el.querySelectorAll(".hm-table__th").length;

      var ths = toArray(el.querySelectorAll(".hm-table__th")).slice(0, cols);
      var headers = [];
      for (var i = 0; i < ths.length; i++) headers.push(clean(ths[i]));

      var tdNodes = toArray(el.querySelectorAll(".hm-table__td"));
      var tds = [];
      for (i = 0; i < tdNodes.length; i++) tds.push(clean(tdNodes[i]));

      var rows = [];
      rows.push(headers);

      for (i = 0; i < tds.length; i += cols) {
        rows.push(tds.slice(i, i + cols));
      }

      var lines = [];
      for (i = 0; i < rows.length; i++) {
        lines.push(rows[i].join("\t"));
      }
      var tsv = lines.join("\n");

      // Copy (old-school textarea method; works basically everywhere)
      var ta = document.createElement("textarea");
      ta.value = tsv;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      ta.style.top = "0";
      document.body.appendChild(ta);
      ta.select();
      var ok = false;
      try { ok = document.execCommand("copy"); } catch (e) {}
      document.body.removeChild(ta);

      if (!ok) {
        this.showErrorMessage("Copy to clipboard blocked by browser.")
      } else {
        const tl = new TimelineMax({
          onComplete: () => {
            el.style.boxShadow = null;
          },
        });
        tl.add(TweenMax.fromTo(el, 0.25, { backgroundColor: '#fff' }, { backgroundColor: '#bbdcd8' }));
        tl.add(TweenMax.fromTo(el, 0.5, { backgroundColor: '#bbdcd8' }, { backgroundColor: '#fff' }));
        this.showSuccessMessage();
      }
      // optional: feedback
      // console.log("Copied " + rows.length + " rows as TSV.");
    },
  },
};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
@import '~styles/import';

.hm-table__td {
  &--inactive {
    background: rgba($gray, 0.75);
  }

  &.weekend {
    background-color: rgba($mint, 0.15);
  }

  &.blur {
    text-shadow: 0px 0px 10px #000!important;
    color: rgba(0,0,0,0.0)!important;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/pages/PageCockpit.vue